import { useTheme } from '@mui/material';
import { isString } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';

import { Omit } from '@/components/charts/Legend/Legend.stories';
import { TileProps } from '@/components/diagrams/components/Tile';
import { TileNode } from '@/components/diagrams/FlowChart';
import { useNode } from '@/components/diagrams/FlowChart/hooks/useNode';
import { FormConfigurationProvider } from '@/components/form/context/FormConfigurationContext';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import {
  FormProvider,
  useForm,
  useFormContext,
  useSubmitSuccessHandler,
} from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { GraphVizNodeComponentType } from '@/modules/graphViz/graphVizNodeConfig';
import { TileNotesFieldWithGenerate } from '@/modules/summaryPanels/TileNotesSummaryPanel/TileNotesFieldWithGenerate';
import { AfterDeath, UpdateGraphVisualizationGroupInput } from '@/types/schema';

import { GraphVizGroupBasicDetailsForm } from '../../graphViz/graphVizGroup/GraphVizGroupBasicDetailsForm';
import { EstateWaterfallSectionMultiSelectTable } from '../components/EstateWaterfallSectionMulitSelectTable/EstateWaterfallSectionMultiSelectTable';
import { OnSelectionChange } from '../components/EstateWaterfallSectionMulitSelectTable/EstateWaterfallSectionMultiSelectTable.utils';
import { useManageNodeGroup } from './useManageNodeGroup';

interface ManageGroupNode {
  id: string;
  heading: string;
  description: string;
  remove?: () => void;
}

export type ManageGroupFormShape = Omit<
  UpdateGraphVisualizationGroupInput,
  'nodeIds'
> & {
  nodes: ManageGroupNode[];
  componentType: GraphVizNodeComponentType;
  applyToAllSections?: boolean;
  hideGroupContents: boolean;
};

interface ManageGroupModalInnerProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  namespace: AfterDeath;
  nodeId: string;
}

function ManageGroupModalInner({
  isOpen,
  onClose,
  groupId,
  namespace,
  nodeId,
}: ManageGroupModalInnerProps) {
  const theme = useTheme();
  const { formRef, handleSave } = useFormSaveHandler();
  const { control, formState, handleSubmit, setValue, reset } =
    useFormContext<ManageGroupFormShape>();

  const { saveManageNodeGroup } = useManageNodeGroup({ groupId, nodeId });

  const watchedNodes = useWatch({ control, name: 'nodes' });
  const currentNodeIds = useMemo(
    () => watchedNodes?.map((node) => node.id) ?? [],
    [watchedNodes]
  );
  const displayName = useWatch({ control, name: 'displayName' });

  const onSubmit = handleSubmit((formData) => {
    return saveManageNodeGroup(formData);
  });

  const handleClose = () => {
    onClose();
  };

  const handleUngroupAll = () => {
    setValue('nodes', []);
    handleSave();
  };

  const onSuccess = () => {
    onClose();
    setTimeout(() => {
      reset();
    }, theme.transitions.duration.leavingScreen);
  };

  useSubmitSuccessHandler(onSuccess);

  const defaultSelectedRowIds = useMemo(() => {
    return (
      formState.defaultValues?.nodes?.flatMap((node) => node?.id ?? []) ?? []
    );
  }, [formState.defaultValues?.nodes]);

  const onSelectionChange = useCallback<OnSelectionChange>(
    ({ selectedRowIds, rows }) => {
      const selectedRows = rows.filter((row) => {
        return selectedRowIds.includes(row.id);
      });
      const newNodes = selectedRows.map((row) => ({
        _id: row.id,
        id: row.id,
        heading: row.entity.lineOne,
        description: row.entity.lineTwo,
      }));

      setValue('nodes', newNodes);
    },
    [setValue]
  );

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      heading="Group settings"
      rightHeaderContent={null}
      actions={
        <FormModalActions.Provider formState={formState}>
          <FormModalActions.DestructiveButton onClick={handleUngroupAll}>
            Ungroup all
          </FormModalActions.DestructiveButton>
          <FormModalActions.CancelButton onClick={handleClose} />
          <FormModalActions.SaveButton onClick={handleSave}>
            Save changes
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <GraphVizGroupBasicDetailsForm<ManageGroupFormShape>
          slots={{
            SelectTable: (
              <EstateWaterfallSectionMultiSelectTable
                namespace={namespace}
                defaultSelectedRowIds={defaultSelectedRowIds}
                onSelectionChange={onSelectionChange}
              />
            ),
            NotesField: (
              <TileNotesFieldWithGenerate<ManageGroupFormShape>
                mode="group"
                control={control}
                fieldName="description"
                nodeIds={currentNodeIds}
                namespace={namespace}
                groupDisplayName={displayName ?? undefined}
              />
            ),
          }}
        />
      </form>
    </FormModal>
  );
}

interface ManageGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  namespace: AfterDeath;
  groupedTiles: (TileProps & { tileId: string; nodeId: string })[];
  groupName?: string | null;
  groupDescription?: string | null;
  groupId: string;
}

export function ManageGroupModal({
  isOpen,
  onClose,
  namespace,
  groupedTiles,
  groupName,
  groupDescription,
  groupId,
}: ManageGroupModalProps) {
  const node = useNode<TileNode>();

  const formMethods = useForm<ManageGroupFormShape>({
    defaultValues: {
      displayName: groupName,
      description: groupDescription,
      namespace,
      componentType: node.data.componentType ?? GraphVizNodeComponentType.Tile,
      applyToAllSections: false,
      hideGroupContents:
        node.data.estateWaterfallConfig?.hideGroupContents ?? false,
      nodes: groupedTiles.map((tile) => ({
        _id: tile.tileId,
        id: tile.nodeId,
        heading: tile.lineOne as string,
        description: isString(tile.lineTwo) ? tile.lineTwo : undefined,
      })),
    },
  });

  const { fields, remove } = useFieldArray({
    control: formMethods.control,
    name: 'nodes',
    keyName: '_id',
  });

  fields.forEach((field, index) => {
    field.remove = () => remove(index);
  });

  return (
    <FormProvider {...formMethods}>
      <FormConfigurationProvider
        value={{ optionalDisplayType: 'optional-label' }}
      >
        <ManageGroupModalInner
          isOpen={isOpen}
          onClose={onClose}
          groupId={groupId}
          namespace={namespace}
          nodeId={node.id}
        />
      </FormConfigurationProvider>
    </FormProvider>
  );
}
